import axios from 'axios';
import {datasetSource} from './datasetSources';
import {decodeAgentResponse} from './resolutionUtils';

/**
 * Whole number from an API value, or null when there is not one.
 *
 * Null rather than zero on purpose, all the way through. The backend treats
 * an absent total as "nobody knows how many there are" and refuses to state
 * a share against it; turning it into 0 here would turn that refusal into a
 * confident wrong answer.
 *
 * @param {*} value API value
 * @return {number|null} the number, or null
 */
function wholeNumber(value) {
	if (value === null || value === undefined || value === '') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

/**
 * One row, reduced to the fields its source is willing to hand over.
 * @param {object} row API row
 * @param {Array<string>|null} fields declared fields, or null for all
 * @return {object} the row, trimmed
 */
function trimRow(row, fields) {
	if (!fields) return row;
	const kept = {};
	fields.forEach((field) => {
		if (row && Object.prototype.hasOwnProperty.call(row, field)) {
			kept[field] = row[field];
		}
	});
	return kept;
}

/**
 * Build the query string for a source from the agent's named filters.
 *
 * A filter the source does not declare is dropped rather than passed
 * through. Canvas usually ignores a parameter it does not recognise, which
 * returns more rows than were asked for and gives no sign that it did - the
 * quietest way to get a wrong answer. The backend refuses these too; this is
 * the second lock on the same door, because the browser is the one that
 * actually builds the URL.
 *
 * @param {object} source declared source
 * @param {object} filters filters the agent asked for
 * @param {object} parameters paging parameters
 * @return {string} query string, without the leading "?"
 */
function queryFor(source, filters = {}, parameters = {}) {
	const parts = [];
	if (source.rowsField) {
		const limit = Math.min(
			Math.max(wholeNumber(parameters.limit) ?? source.maxRows, 1),
			source.maxRows,
		);
		parts.push(`limit=${limit}`);
		parts.push(`offset=${Math.max(wholeNumber(parameters.offset) ?? 0, 0)}`);
	}
	Object.keys(filters || {}).forEach((name) => {
		const canvasName = source.filters[name];
		if (!canvasName) return;
		const value = filters[name];
		if (value === null || value === undefined || value === '') return;
		parts.push(`${canvasName}=${encodeURIComponent(String(value))}`);
	});
	return parts.join('&');
}

/**
 * Read one declared dataset and hand back what a count can be made from.
 *
 * This is the executor the agent's data reads run through. It exists here
 * rather than in the backend because this browser already holds the user's
 * token, already decrypts the response, and already handles the token
 * rotating mid-session - and because reading as the signed-in user is what
 * stops the agent reporting forty-seven Access Points to someone permitted
 * to see twelve.
 *
 * What travels back is a page of trimmed rows and the true total beside it.
 * Both halves matter: the rows are what a grouping is made from, and the
 * total is what stops "two of the four I looked at" being said as "half".
 *
 * @param {object} command fetch_dataset command
 * @param {object} envelope request correlation, carrying the turn's signal
 * @return {Promise<object>} payload the backend can count
 */
export async function fetchDataset(command = {}, envelope = {}) {
	const sourceId = command.params?.source_id;
	const source = datasetSource(sourceId);
	// Refused synchronously, the way an unknown section or statistic is: a
	// source nobody declared is a bug in the plan, not a failed read.
	if (!source) throw new Error('unknown_dataset_source');

	// L07. This is the one handler that makes a network request, and it was
	// the one handler the turn's abort could not reach - so a read given up
	// on at fifteen seconds kept fetching, decoded a page of device data
	// nobody was waiting for, and resolved into a turn that had moved on.
	const signal = envelope?.signal;
	if (signal?.aborted) throw new Error('command_abandoned');

	const query = queryFor(source, command.params?.filters, command.params?.parameters);
	const url = query ? `${source.url}?${query}` : source.url;
	const {data} = await axios.get(url, signal ? {signal} : undefined);
	const document = decodeAgentResponse(data) || {};

	if (!source.rowsField) {
		// An aggregate is already the answer. Handed over whole, because
		// which figure inside it was wanted is the backend's business and
		// trimming it here would mean this file knowing that too.
		return {payload: document.data ?? document};
	}

	const rows = Array.isArray(document[source.rowsField])
		? document[source.rowsField]
		: [];
	return {
		payload: {
			[source.rowsField]: rows
				.slice(0, source.maxRows)
				.map((row) => trimRow(row, source.fields)),
			[source.totalField]: wholeNumber(document[source.totalField]),
		},
	};
}
